import router from '../plugins/router'

const appStore = useAppStore()
const {
  miniDrawer,
  navDrawer,
} = storeToRefs(appStore)

async function openDrawer() {
  if (miniDrawer) {
    await new Promise(resolve => setTimeout(resolve, 50))
    miniDrawer.value = false
    navDrawer.value = true
  }
}

export const useGuideStore = defineStore('guide', {
  state: () => ({
    dialog: false,
    guides: {
      features: {
        icon: '$mdiFeatureSearch',
        subtitle: 'View current features of the election portal.',
        title: 'Features',
        steps: [
          {
            target: '.features-step-tiles',
            content: `
              <h3>Account Status Tiles</h3>
              Browse insights into current and past elections.
            `,
            onBefore: async () => {
              await router.push('/dashboard')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
          },
          {
            target: '.features-step-actionItems',
            content: `
              <h3>Action Items</h3>
              View outstanding action items related to recent elections or orders.
            `,
          },
          {
            target: '.features-step-upcoming',
            content: `
              <h3>Upcoming Elections</h3>
              See upcoming elections and start the order process from here.
            `,
          },

          {
            target: '.Elections-link',
            content: `
              <h3>Elections Page</h3>
              Navigate to the Elections page. Select next to continue. 
            `,
            onBefore: async () => {
              openDrawer()
            },
          },
          {
            target: '.features-step-elections',
            content: `
              <h3>Elections Table</h3>
              View all current<br> and previous elections.
            `,
            onBefore: async () => {
              await router.push('/elections')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            backStep: async () => {
              await router.push('/dashboard')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            placement: 'left',
          },
          {
            target: '.Orders-link',
            content: `
              <h3>Orders Page</h3>
              Navigate to the Orders page. Click Next to continue.
            `,
            onBefore: async () => {
              openDrawer()
            },
          },
          {
            target: '.features-step-orders',
            content: `
              <h3>Orders Table</h3>
              View all current and<br> previous orders with statuses.
            `,
            onBefore: async () => {
              await router.push('/orders')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            backStep: async () => {
              await router.push('/elections')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            placement: 'left',
          },

          {
            target: '.Preferences-link',
            content: `
              <h3>Preferences Page</h3>
              Navigate to the Preferences page. Click Next to continue.
            `,
            onBefore: async () => {
              openDrawer()
            },
          },

          {
            target: '.features-step-preferences',
            content: `
              <h3>Preferences Menu</h3>
              Manage global preferences for elections and orders across the app.
            `,
            onBefore: async () => {
              await router.push('/preferences')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            backStep: async () => {
              await router.push('/elections')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            placement: 'left',
          },

          {
            target: '.Settings-link',
            content: `
              <h3>Settings Page</h3>
              Navigate to the Settings page. Click Next to continue.
            `,
            onBefore: async () => {
              openDrawer()
            },
          },
          {
            target: '.features-step-settings',
            content: `
              <h3>Settings Menu</h3>
              Manage account settings, including changing your password.
            `,
            onBefore: async () => {
              await router.push('/settings')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            backStep: async () => {
              await router.push('/preferences')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            placement: 'left',
          },
        ],
      },
      elections: {
        icon: '$mdiArchive',
        subtitle: 'Walk through creating an election.',
        title: 'Elections',
        steps: [
          {
            target: '.features-step-upcoming',
            content: `
              <h3>Upcoming Elections</h3>
              Any current elections can be viewed on the dashboard.
            `,
          },
          {
            target: '.Elections-link',
            content: `
              <h3>Elections Page</h3>
              Navigate to the Elections page. Select next to continue. 
            `,
            onBefore: async () => {
              openDrawer()
            },
          },
          {
            target: '.features-step-elections',
            content: `
              <h3>Elections Table</h3>
              View all current<br> and previous elections.
            `,
            onBefore: async () => {
              await router.push('/elections')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            backStep: async () => {
              await router.push('/dashboard')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            placement: 'left',
          },
          {
            target: '.elections-step-previous',
            content: `
              <h3>Previous Orders</h3>
              See how many orders were placed for each election.
            `,
          },
          {
            target: '.elections-step-newElection',
            content: '<h3>New Election Button</h3>Open the new election creation menu.',
          },
          {
            target: '.elections-step-details',
            content: '<h3>Election Details Form</h3>Fill in election type and date. This will affect product ordering options.',
            onBefore: () => {
              if (!document.querySelector<HTMLElement>('.elections-step-close')) {
                document.querySelector<HTMLElement>('.elections-step-newElection')?.click()
              }
            },
            backStep: async () => {
              document.querySelector<HTMLElement>('.elections-step-close')?.click()
            },
          },
          {
            target: '.elections-step-create',
            content: '<h3>Create Election Button</h3>Selecting Create finalizes the election and lets you begin ordering products.',
          },
        ],
      },
      orders: {
        icon: '$mdiCartPlus',
        subtitle: 'Walk through submitting an order.',
        title: 'Orders',
        steps: [
          {
            target: '.order-step-orderNow',
            content: '<h3>Order Now Option</h3>An order can be started from the Upcoming Elections menu on the dashboard.',
          },
          {
            target: '.order-step-orderPage',
            content: '<h3>Order Entry Page</h3>This is the main order entry page.',
            onBefore: async () => {
              document.querySelector<HTMLElement>('.order-step-orderNow')?.click()
              await new Promise(resolve => setTimeout(resolve, 400))
            },
            backStep: async () => {
              await router.push('/dashboard')
              await new Promise(resolve => setTimeout(resolve, 150))
            },
          },
          {
            target: '.pa-3',
            content: '<h3>Product Types</h3>This list contains all possible product types, with filters located on the left side of the screen to narrow down options.',
            onBefore: async () => {
              document.querySelector<HTMLElement>('.order-step-browseAll')?.click()
              await new Promise(resolve => setTimeout(resolve, 400))
            },
            backStep: async () => {
              document.querySelector<HTMLElement>('.elections-step-startOver')?.click()
              await new Promise(resolve => setTimeout(resolve, 400))
            },
          },
          {
            target: '.order-step-typeSelect',
            content: '<h3>Product Type Select</h3>Click a specific item to continue the ordering process. We will use this product type as an example.',
            onBefore: async () => {
              const el = document.querySelector<HTMLElement>('.order-step-typeSelect')

              if (el && !el.classList.contains('bg-grey-lighten-4')) {
                el.click()
                await new Promise(resolve => setTimeout(resolve, 200))
              }
            },
            backStep: async () => {
              const el = document.querySelector<HTMLElement>('.order-step-typeSelect')

              if (el && el.classList.contains('bg-grey-lighten-4')) {
                el.click()
                await new Promise(resolve => setTimeout(resolve, 200))
              }
            },
          },
          {
            target: '.order-step-nextStep',
            content: '<h3>Continue to Next Step</h3>Continue to the next step in the order process.',
          },
          {
            target: '.order-step-orderQuantities',
            content: '<h3>Edit Order Quantities</h3>Adjust quantities before adding the item to your cart. The quantity menu will have different options depending on the product type.',
            onBefore: async () => {
              document.querySelector<HTMLElement>('.order-step-nextStep')?.click()
              await new Promise(resolve => setTimeout(resolve, 200))
            },
            backStep: async () => {
              document.querySelector<HTMLElement>('.order-step-backStep')?.click()
              await new Promise(resolve => setTimeout(resolve, 200))
            },
          },
          {
            target: '.order-step-quantityInput input[type="number"]',
            content: '<h3>Enter Order Quantity</h3>Enter a quantity for at least one line to continue.',
            onAfter: async () => {
              const input = document.querySelector<HTMLInputElement>('.order-step-quantityInput input[type="number"]')
              if (input) {
                input.value = '50'
                input.dispatchEvent(new Event('input', { bubbles: true }))
              }
            },
            backStep: async () => {
              const input = document.querySelector<HTMLInputElement>('.order-step-quantityInput input[type="number"]')
              if (input) {
                input.value = '0'
                input.dispatchEvent(new Event('input', { bubbles: true }))
              }
            },
          },
          // {
          //   target: '.order-step-nextStep',
          //   content: '<h3>Continue to Next Step</h3>Continue to the next step in the order process.',
          // },

          // {
          //   target: '.order-step-orderPage1',
          //   content: '<h3>Ballot Options</h3> The options menu displays different options based on product type. Ballot order options include back panel verbiage and language selection.',
          //   onBefore: async () => {
          //     document.querySelector<HTMLElement>('.order-step-nextStep')?.click()
          //     await new Promise(resolve => setTimeout(resolve, 100))
          //   },
          //   backStep: async () => {
          //     document.querySelector<HTMLElement>('.order-step-backStep')?.click()
          //     await new Promise(resolve => setTimeout(resolve, 200))
          //   },
          // },
          {
            target: '.order-step-cart',
            content: '<h3>Add to Cart</h3>Add current item to cart.',
          },
          {
            target: '.cart-contents',
            content: '<h3>Cart Contents</h3>View the current contents of your cart. Items can be adjusted or deleted individually.',
            onBefore: async () => {
              document.querySelector<HTMLElement>('.order-step-cart')?.click()
              await new Promise(resolve => setTimeout(resolve, 750))
            },
            backStep: 'Skip',
          },

          {
            target: '.order-step-checkout',
            content: '<h3>Checkout</h3>Click to begin the checkout process.',
          },
          {
            target: '.order-step-checkoutMain',
            content: '<h3>Entire Checkout menu</h3>Configure billing, shipping, and order-specific file uploads.',
            onBefore: async () => {
              document.querySelector<HTMLElement>('.order-step-checkout')?.click()
              await new Promise(resolve => setTimeout(resolve, 250))
            },
            backStep: async () => {
              // add step to go to dashboard and open cart(?)
            },
          },
          {
            target: '.order-step-billing',
            content: '<h3>Edit Billing Details</h3>Billing details are preconfigured based on county defaults (editable in the preferences tab).',
          },
          {
            target: '.order-step-shipping',
            content: '<h3>Edit Shipping Details</h3>Shipping details are prefilled (editable in the preferences tab) or can be edited per order.',
            onBefore: async () => {
              document.querySelector<HTMLElement>('.order-step-checkoutNext')?.click()
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            backStep: async () => {
              document.querySelector<HTMLElement>('.order-step-checkoutBack')?.click()
              await new Promise(resolve => setTimeout(resolve, 250))
            },
          },
          {
            target: '.order-step-address',
            content: '<h3>Select Preconfigured Address</h3>Preconfigured default addresses can be changed in the preferences tab.',
          },
          {
            target: '.order-step-uploads',
            content: '<h3>File Uploads</h3>Upload order related files, including ballot files or mailing files. NOTE: For uploads of 10+ files, please zip them first.',
            onBefore: async () => {
              document.querySelector<HTMLElement>('.order-step-checkoutNext')?.click()
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            backStep: async () => {
              document.querySelector<HTMLElement>('.order-step-checkoutBack')?.click()
              await new Promise(resolve => setTimeout(resolve, 250))
            },
          },
          {
            target: '.order-step-summary',
            content: '<h3>Finalize Purchase Details</h3>Review order details before submitting.',
            onBefore: async () => {
              document.querySelector<HTMLElement>('.order-step-checkoutNext')?.click()
              await new Promise(resolve => setTimeout(resolve, 150))
            },
            backStep: async () => {
              document.querySelector<HTMLElement>('.order-step-checkoutBack')?.click()
              await new Promise(resolve => setTimeout(resolve, 250))
            },
          },
          {
            target: '.order-step-submit',
            content: '<h3>Finalize and Submit Order</h3>',
          },
        ],
      },
      files: {
        icon: '$mdiUpload',
        subtitle: 'Walk through how to upload files.',
        title: 'Files',
        steps: [
          {
            target: '.Browser-link',
            content: `
              <h3>File Browser Link</h3>
              Used to navigate to the File Browser/Upload page. Select next to continue. 
            `,
            onBefore: async () => {
              openDrawer()
            },
          },
          {
            target: '.be-ItemList',
            content: `
              <h3>File Browser</h3>
              <ul class="ml-2">
                <li>
                  <b>Election Specific: </b>Contains files specific to the most recent election.
                </li>
                <li>
                  <b>Inbox: </b>Contains files uploaded by you across the portal.
                </li>
                <li>
                  <b>Outbox: </b>Contains files sent by Phoenix Graphics for you to review/approve.
                </li>
              </ul>
            `,
            onBefore: async () => {
              document.querySelector<HTMLElement>('.Browser-link')?.click()
              await new Promise(resolve => setTimeout(resolve, 1500))
            },
          },
          {
            target: '.files-step-uploadsButton',
            content: '<h3>Upload files here</h3>Upload files directly to the Inbox folder.',
          },
          {
            target: '#box-content-uploader',
            content: `
              <h3>File Upload Menu</h3>
              Drop files here to be uploaded. NOTE: For uploads of 10+ files, please zip them first.
            `,
            onBefore: async () => {
              document.querySelector<HTMLElement>('.files-step-uploadsButton')?.click()
              await new Promise(resolve => setTimeout(resolve, 500))
            },
            backStep: async () => {
              document.querySelector<HTMLElement>('.bcu-footer-left button')?.click()
              await new Promise(resolve => setTimeout(resolve, 500))
            },
          },
        ],
      },
    },
    onboard: false,
    steps: [],
    tour: false,
  }),
  getters: {},
  actions: {
    async reset() {
      this.$patch({
        dialog: false,
        steps: [],
        tour: false,
        onboard: false,
      })
    },
    async startGuide(guide: any) {
      await router.push('/dashboard')
      await new Promise(resolve => setTimeout(resolve, 250))

      this.$patch({
        tour: true,
        dialog: false,
        onboard: false,
        steps: guide.steps,
      })
    },
    async startOnboarding() {
      this.$patch({
        tour: false,
        dialog: true,
        onboard: true,
      })
    },
  },
})
